/**
 * Charting — how the oversight program is performing, and what sits underneath.
 *
 * Precision COA is the table of record; this is its effectiveness view.
 * Three levels, each with its own trend against its benchmark, and you can
 * walk down without losing the whole:
 *
 *   Program areas  →  Measures  →  Case types
 *   (ODAG, CDAG…)     (ODAG Table 1…)  (Standard OD, Expedited, Part B…)
 *
 * Month over month, quarter over quarter, year over year — every number
 * carries its change against the prior period. Charts or Table: the table is
 * a calendar grid (rows open into what is underneath them, one column per
 * period, every cell tinted met or missed) and downloads for Excel.
 *
 * Reads only the monitoring board feed (counts and rates, org-scoped). Every
 * case type the feed carries is used — a measure's rate is the sum of its
 * case types, never the first one alone.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Tooltip, Legend, Filler,
} from 'chart.js';

import { exportChartingPdf } from './exportChartingPdf';
import { fmtMeasure, deltaSuffix, normalizeUnit } from './measureFormat';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

const API = process.env.REACT_APP_API_URL || '';
const ORANGE = '#F58220';
const EMERALD = '#10b981';
const ROSE = '#f43f5e';
const SLATE = '#94a3b8';
const CASE_COLORS = ['#F58220', '#2563eb', '#7c3aed', '#0d9488', '#db2777', '#65a30d', '#0891b2', '#475569'];
const YEAR_COLORS = ['#cbd5e1', '#94a3b8', '#f59e0b', ORANGE];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymLabel = (ym) => { const [y, m] = String(ym).split('-').map(Number); return `${MONTHS_SHORT[m - 1]} ${String(y).slice(2)}`; };
const quarterOf = (ym) => { const [y, m] = String(ym).split('-').map(Number); return `${y}-Q${Math.ceil(m / 3)}`; };
const yearOf = (ym) => String(ym).slice(0, 4);
const qLabel = (q) => `Q${q.split('-Q')[1]} ${q.slice(2, 4)}`;
const rate1 = (num, den) => (den > 0 ? Math.round((num / den) * 1000) / 10 : null);

// Program areas — the compliance-facing names, derived from each measure key.
const AREAS = [
  { key: 'odag', label: 'ODAG', full: 'Organization Determinations, Appeals & Grievances' },
  { key: 'cdag', label: 'CDAG', full: 'Coverage Determinations, Appeals & Grievances (Part D)' },
  { key: 'snpcc', label: 'SNPCC', full: 'Special Needs Plan Care Coordination' },
  { key: 'reporting', label: 'Reporting', full: 'Regulatory reporting timeliness' },
  { key: 'privacy', label: 'Privacy', full: 'Privacy incident handling' },
  { key: 'issues', label: 'Issues', full: 'Compliance issues remediation' },
  { key: 'cap', label: 'CAPs', full: 'Corrective action plan closure' },
  { key: 'adhoc', label: 'Your measures', full: 'Measures the organization defined itself' },
];
function areaOfMeasure(measureKey) {
  const k = String(measureKey || '');
  if (k.startsWith('odag')) return 'odag';
  if (k.startsWith('cdag')) return 'cdag';
  if (k.startsWith('snpcc')) return 'snpcc';
  if (k.startsWith('reporter')) return 'reporting';
  if (k.startsWith('privacy')) return 'privacy';
  if (k.startsWith('cil')) return 'issues';
  if (k.startsWith('cap')) return 'cap';
  return 'adhoc';
}

// Case type names arrive as the Scrubber's scenario labels; shorten the
// protocol prefix so the tile reads "Expedited", not "ODAG Table 1 — Expedited".
function shortCase(label, measureLabel) {
  let s = String(label || '');
  const m = String(measureLabel || '');
  if (m && s.startsWith(m)) s = s.slice(m.length);
  s = s.replace(/^[\s—–:-]+/, '');
  return s || String(label || 'Case type');
}

/** Met (true), missed (false), or nothing to judge (null). */
function judge(rate, benchmark, direction) {
  if (rate === null || rate === undefined || benchmark === null || benchmark === undefined) return null;
  return direction === 'lower' ? rate <= benchmark : rate >= benchmark;
}

/**
 * Roll a period map (ym -> {num, den, status, defs}) into the chosen grain,
 * within a year. Month buckets keep the stored verdict when there is one;
 * coarser buckets are judged from their summed rate against the benchmark.
 */
function bucketize(periods, grain, year, benchmark, direction) {
  const keyOf = grain === 'month' ? (ym) => ym : grain === 'quarter' ? quarterOf : yearOf;
  const agg = new Map();
  for (const [ym, p] of periods) {
    if (year !== 'all' && yearOf(ym) !== year) continue;
    const k = keyOf(ym);
    if (!agg.has(k)) agg.set(k, { num: 0, den: 0, defs: 0, met: 0, missed: 0, months: 0, value: null, merged: false, share: false });
    const b = agg.get(k);
    b.num += p.num; b.den += p.den; b.defs += p.defs; b.months += 1;
    if (p.value !== null && p.value !== undefined) b.value = p.value; // last value wins (value-only measures)
    if (p.status === 'met') b.met += 1;
    if (p.status === 'missed') b.missed += 1;
    if (p.merged) b.merged = true;
    if (p.share) { b.share = true; b.votes = (b.votes || 0) + (p.votes || 0); b.metVotes = (b.metVotes || 0) + (p.metVotes || 0); }
  }
  return [...agg.entries()].sort(([x], [y]) => x.localeCompare(y)).map(([k, b]) => {
    const rate = b.den > 0 ? rate1(b.num, b.den) : b.value;
    let ok;
    // A measure's own month keeps its stored verdict. A rolled-up row (an
    // area, the whole program) with ONE benchmark is judged on its own rate
    // against it — ODAG at 97.4% against 95% is met, whatever a table under
    // it did (operator ruling 2026-08-28). With mixed benchmarks the roll-up
    // is met only when nothing under it missed.
    if (grain === 'month' && (b.met || b.missed) && !(b.merged && benchmark !== null && benchmark !== undefined)) ok = b.met > 0 && b.missed === 0;
    else ok = judge(rate, benchmark, direction);
    if (grain !== 'month' && b.met + b.missed === 0 && ok !== null) { ok ? (b.met = 1) : (b.missed = 1); }
    return { key: k, label: grain === 'month' ? ymLabel(k) : grain === 'quarter' ? qLabel(k) : k, rate, ok, ...b };
  });
}

/** Sum several period maps into one (for areas and the whole program). */
function mergePeriods(list) {
  const out = new Map();
  for (const periods of list) {
    for (const [ym, p] of periods) {
      if (!out.has(ym)) out.set(ym, { num: 0, den: 0, defs: 0, met: 0, missed: 0, status: null, value: null, merged: true, votes: 0, metVotes: 0 });
      const o = out.get(ym);
      o.num += p.num; o.den += p.den; o.defs += p.defs;
      if (p.status === 'met') o.met += 1;
      if (p.status === 'missed') o.missed += 1;
      // A value-only measure (a scorecard KPI, a count) cannot be summed with
      // the others; it rolls up as one vote, met or missed.
      if (!(p.den > 0) && (p.status === 'met' || p.status === 'missed')) { o.votes += 1; if (p.status === 'met') o.metVotes += 1; }
    }
  }
  // A merged month is "met" only when nothing under it missed. Where nothing
  // could be summed, the roll-up's rate is the share of measures met.
  for (const o of out.values()) {
    o.status = o.met + o.missed === 0 ? null : (o.missed === 0 ? 'met' : 'missed');
    if (o.den === 0 && o.votes > 0) { o.num = o.metVotes; o.den = o.votes; o.share = true; }
  }
  return out;
}

// ── Small pieces ─────────────────────────────────────────────────────────────
function Delta({ value, suffix = ' pts', invert = false }) {
  if (value === null || value === undefined || Number.isNaN(value)) return <span className="text-xs text-slate-400">no prior period</span>;
  const good = invert ? value <= 0 : value >= 0;
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '•';
  return (
    <span className={`text-xs font-semibold ${value === 0 ? 'text-slate-500' : good ? 'text-emerald-600' : 'text-rose-600'}`}>
      {arrow} {Math.abs(Math.round(value * 10) / 10)}{suffix}
    </span>
  );
}

function Sparkline({ points, benchmark, color = ORANGE, pct = true }) {
  const vals = points.map((p) => p.rate).filter((v) => v !== null && v !== undefined);
  if (vals.length < 2) return <div className="h-7 text-[10px] text-slate-400 flex items-center">one period so far</div>;
  const w = 96; const h = 28;
  const all = benchmark !== null && benchmark !== undefined ? [...vals, benchmark] : vals;
  const min = Math.max(0, Math.min(...all) - 3); const max = (pct ? Math.min(100, Math.max(...all) + 3) : Math.max(...all) * 1.1) || (pct ? 100 : 1);
  const y = (v) => h - 3 - ((v - min) / Math.max(1, max - min)) * (h - 6);
  const x = (i) => 3 + (i / Math.max(1, points.length - 1)) * (w - 6);
  const d = points.map((p, i) => (p.rate === null ? null : `${x(i)},${y(p.rate)}`)).filter(Boolean).join(' ');
  const last = points[points.length - 1];
  return (
    <svg width={w} height={h} className="block" aria-hidden="true">
      {benchmark !== null && benchmark !== undefined && (
        <line x1="3" x2={w - 3} y1={y(benchmark)} y2={y(benchmark)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="3 2" opacity="0.7" />
      )}
      <polyline points={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {last && last.rate !== null && (
        <circle cx={x(points.length - 1)} cy={y(last.rate)} r="2.6" fill={last.ok === false ? ROSE : last.ok ? EMERALD : color} />
      )}
    </svg>
  );
}

function Segmented({ value, onChange, options, ariaLabel }) {
  return (
    <div role="tablist" aria-label={ariaLabel} className="inline-flex rounded-xl bg-slate-100 p-1 gap-0.5">
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value}
          onClick={() => onChange(o.value)} title={o.title || undefined}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${value === o.value ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Pill({ ok }) {
  if (ok === null || ok === undefined) return <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">no benchmark</span>;
  return ok
    ? <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">Met</span>
    : <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-200">Missed</span>;
}

/** One tile per thing underneath the current level. Click to walk into it. */
function Tile({ title, subtitle, buckets, benchmark, color, onOpen, count, hint, unit = 'percent', direction = 'higher' }) {
  const cur = buckets[buckets.length - 1] || null;
  const prev = buckets[buckets.length - 2] || null;
  const delta = cur && prev && cur.rate !== null && prev.rate !== null ? cur.rate - prev.rate : null;
  return (
    <button type="button" onClick={onOpen} title={hint || 'Open'}
      className="text-left rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:border-orange-300 hover:shadow-md transition group">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-800 leading-snug"
            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', ...(color ? { color } : {}) }}
            title={title}>{title}</p>
          {subtitle && <p className="text-[11px] text-slate-500 truncate" title={subtitle}>{subtitle}</p>}
        </div>
        {count !== undefined && <span className="shrink-0 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{count}</span>}
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <p className={`text-2xl font-bold tabular-nums leading-none ${cur?.ok === false ? 'text-rose-700' : cur?.ok ? 'text-emerald-700' : 'text-slate-800'}`}>
            {fmtMeasure(cur?.rate, unit)}
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            <Pill ok={cur ? cur.ok : null} />
            <Delta value={delta} suffix={deltaSuffix(unit)} invert={direction === 'lower'} />
          </div>
        </div>
        <Sparkline points={buckets} benchmark={benchmark} color={color || ORANGE} pct={unit === 'percent'} />
      </div>
      {benchmark !== null && benchmark !== undefined && (
        <p className="mt-2 text-[11px] text-slate-400">Benchmark {fmtMeasure(benchmark, unit)} · {cur ? `${cur.label}` : ''}</p>
      )}
      <p className="mt-1 text-[11px] text-orange-600 opacity-0 group-hover:opacity-100 transition">Open →</p>
    </button>
  );
}

function StatCard({ label, value, sub, tone = 'slate' }) {
  const tones = { slate: 'text-slate-800', good: 'text-emerald-700', bad: 'text-rose-700', brand: 'text-orange-600' };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${tones[tone]}`}>{value}</p>
      {sub && <div className="mt-1">{sub}</div>}
    </div>
  );
}

function downloadCsv(filename, rows) {
  const esc = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const text = rows.map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ── The page ─────────────────────────────────────────────────────────────────
/**
 * Props:
 *   scope            null (the organization's own oversight) or 'fdr' (work on registered FDRs);
 *                    the server keeps the two apart, the board only asks for one.
 *   measuresOverride an already shaped measure list (id, label, entity, area, benchmark,
 *                    direction, unit, periods, cases) from another feed, e.g. FDR scorecards;
 *                    when given, nothing is fetched here.
 *   areasOverride    the program areas for that list [{ key, label, full }].
 *   title / docTitle the heading on screen and the name on the CSV and PDF.
 *   emptyHint        what to do when there is nothing to chart yet.
 */
export default function ChartingBoard({ scope = null, orgName = '', measuresOverride = null, areasOverride = null, title = 'Charting', docTitle = 'COA Charting', emptyHint = 'Sync activities in Precision COA and their results appear here as trends.' }) {
  const [board, setBoard] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(!measuresOverride);
  const areas = areasOverride || AREAS;
  const areaLabelOf = (key) => areas.find((a) => a.key === key)?.label || key;

  const [view, setView] = useState('charts');   // charts | table
  const [grain, setGrain] = useState('month');  // month | quarter | year
  const [year, setYear] = useState('all');
  const [entity, setEntity] = useState('all');
  const [compareYears, setCompareYears] = useState(false);
  const [path, setPath] = useState({ area: null, measure: null, kase: null });
  const [hiddenCases, setHiddenCases] = useState(new Set());
  const [open, setOpen] = useState(new Set()); // table rows that are expanded

  useEffect(() => {
    // The charts follow the board they are mounted on: FDR work appears on the
    // FDR charts, the organization's own oversight on the internal charts.
    if (measuresOverride) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/api/coa/monitoring/board${scope === 'fdr' ? '?scope=fdr' : ''}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d.success) { setBoard(d.board || []); setTrend(d.trend || []); } setLoading(false); })
      .catch(() => setLoading(false));
  }, [scope, measuresOverride]);

  // ── Measures, with every case type underneath them. ──────────────────────
  const measures = useMemo(() => {
    if (measuresOverride) return measuresOverride;
    const map = new Map();
    for (const r of board) {
      if (!r.series_id) continue;
      if (!map.has(r.series_id)) {
        map.set(r.series_id, {
          id: r.series_id,
          label: r.measure_label || 'Measure',
          entity: r.entity_name || 'Internal',
          area: areaOfMeasure(r.measure_key),
          benchmark: r.benchmark !== null && r.benchmark !== undefined ? Number(r.benchmark) : null,
          direction: r.direction === 'lower' ? 'lower' : 'higher',
          unit: normalizeUnit(r.benchmark_unit),
          periods: new Map(),
          cases: new Map(),
        });
      }
      const a = map.get(r.series_id);
      // Data error counts (errors.*) ride beside the timeliness rows; they
      // are deficiencies, never cases, so they never enter a rate.
      const metrics = (r.metrics || []).filter((m) => !/^errors\./.test(String(m.metric_key || '')));
      if (!r.source_period || !metrics.length) continue;
      let num = 0; let den = 0; let defs = 0; let value = null;
      for (const m of metrics) {
        if (m.unit && m.unit !== 'percent') a.unit = normalizeUnit(m.unit);
        const mn = Number(m.numerator || 0); const md = Number(m.denominator || 0);
        num += mn; den += md; defs += Number(m.deficiency_count || 0);
        if (md === 0 && m.value !== null && m.value !== undefined) value = Number(m.value);
        // Case types: only measures with more than one, or a named scenario.
        const label = shortCase(m.metric_label, a.label);
        if (!a.cases.has(label)) {
          a.cases.set(label, {
            label,
            target: m.target !== null && m.target !== undefined ? Number(m.target) : null,
            periods: new Map(),
          });
        }
        const c = a.cases.get(label);
        const cRate = md > 0 ? rate1(mn, md) : (m.value !== null && m.value !== undefined ? Number(m.value) : null);
        const cBench = c.target !== null ? c.target : a.benchmark;
        const cOk = m.threshold_met !== null && m.threshold_met !== undefined && c.target !== null
          ? !!m.threshold_met : judge(cRate, cBench, a.direction);
        c.periods.set(r.source_period, {
          num: mn, den: md, defs: Number(m.deficiency_count || 0), value: md > 0 ? null : cRate,
          status: cOk === null ? null : (cOk ? 'met' : 'missed'),
        });
      }
      if (den > 0 || value !== null) {
        a.periods.set(r.source_period, {
          num, den, defs: Number(r.number_of_deficiencies ?? defs) || 0, value,
          status: ['met', 'missed'].includes(r.monitoring_status) ? r.monitoring_status : null,
        });
      }
    }
    // Trend rows fill any period the board window missed (aggregates only).
    for (const t of trend) {
      const a = map.get(t.series_id);
      if (a && t.source_period && !a.periods.has(t.source_period) && Number(t.denominator) > 0) {
        a.periods.set(t.source_period, { num: Number(t.numerator), den: Number(t.denominator), defs: 0, status: null, value: null });
      }
    }
    // One case type is the measure itself, not a breakdown; only measures with
    // two or more (Standard, Expedited, Part B…) open into case types.
    for (const a of map.values()) if (a.cases.size < 2) a.cases.clear();
    return [...map.values()].filter((a) => a.periods.size > 0).sort((x, y) => x.label.localeCompare(y.label));
  }, [board, trend, measuresOverride]);

  const years = useMemo(() => [...new Set(measures.flatMap((a) => [...a.periods.keys()].map(yearOf)))].sort(), [measures]);
  const entities = useMemo(() => [...new Set(measures.map((a) => a.entity))].sort(), [measures]);
  const scoped = useMemo(() => measures.filter((a) => entity === 'all' || a.entity === entity), [measures, entity]);
  const presentAreas = useMemo(() => areas.filter((ar) => scoped.some((a) => a.area === ar.key)), [scoped, areas]);

  // ── Where we are: program → area → measure → case type. ──────────────────
  const focusMeasure = path.measure ? scoped.find((a) => a.id === path.measure) || null : null;
  const focusCase = focusMeasure && path.kase ? focusMeasure.cases.get(path.kase) || null : null;
  const level = focusCase ? 'case' : focusMeasure ? 'measure' : path.area ? 'area' : 'program';
  const inFocus = useMemo(() => {
    if (focusMeasure) return [focusMeasure];
    if (path.area) return scoped.filter((a) => a.area === path.area);
    return scoped;
  }, [scoped, path.area, focusMeasure]);

  const focusBenchmark = focusCase ? (focusCase.target ?? focusMeasure.benchmark)
    : focusMeasure ? focusMeasure.benchmark
      : (() => { const bs = [...new Set(inFocus.map((a) => a.benchmark).filter((b) => b !== null))]; return bs.length === 1 ? bs[0] : null; })();
  const focusDirection = focusMeasure ? focusMeasure.direction : 'higher';
  // One unit across the focus: a measure's own; otherwise percent unless every
  // measure in view shares another unit (a hold-time area, say).
  const focusUnit = focusMeasure ? focusMeasure.unit
    : (() => { const us = [...new Set(inFocus.map((a) => a.unit))]; return us.length === 1 ? us[0] : 'percent'; })();
  const focusPeriods = useMemo(() => {
    if (focusCase) return focusCase.periods;
    if (focusMeasure) return focusMeasure.periods;
    return mergePeriods(inFocus.map((a) => a.periods));
  }, [focusCase, focusMeasure, inFocus]);
  const buckets = useMemo(() => bucketize(focusPeriods, grain, year, focusBenchmark, focusDirection), [focusPeriods, grain, year, focusBenchmark, focusDirection]);
  const current = buckets[buckets.length - 1] || null;
  const prior = buckets[buckets.length - 2] || null;
  const grainNoun = grain === 'month' ? 'month' : grain === 'quarter' ? 'quarter' : 'year';

  // Case-type lines for a focused measure.
  const caseSeries = useMemo(() => {
    if (!focusMeasure || focusCase) return [];
    return [...focusMeasure.cases.values()].map((c, i) => ({
      label: c.label, color: CASE_COLORS[i % CASE_COLORS.length],
      benchmark: c.target ?? focusMeasure.benchmark,
      buckets: bucketize(c.periods, grain, year, c.target ?? focusMeasure.benchmark, focusMeasure.direction),
    }));
  }, [focusMeasure, focusCase, grain, year]);

  // Year-over-year overlay: the same months, one line per year.
  const yoy = useMemo(() => {
    if (!compareYears) return null;
    const byYear = new Map();
    for (const [ym, p] of focusPeriods) {
      const y = yearOf(ym); const m = Number(ym.slice(5, 7)) - 1;
      if (!byYear.has(y)) byYear.set(y, Array.from({ length: 12 }, () => ({ num: 0, den: 0, value: null })));
      const cell = byYear.get(y)[m]; cell.num += p.num; cell.den += p.den; if (p.value !== null) cell.value = p.value;
    }
    return [...byYear.entries()].sort(([x], [y]) => x.localeCompare(y))
      .map(([y, ms]) => ({ year: y, rates: ms.map((c) => (c.den > 0 ? rate1(c.num, c.den) : c.value)) }));
  }, [compareYears, focusPeriods]);

  // ── Tiles for what sits underneath the current level. ────────────────────
  const tiles = useMemo(() => {
    if (level === 'program') {
      return presentAreas.map((ar) => {
        const list = scoped.filter((a) => a.area === ar.key);
        const bs = [...new Set(list.map((a) => a.benchmark).filter((b) => b !== null))];
        const bench = bs.length === 1 ? bs[0] : null;
        return { key: ar.key, title: ar.label, subtitle: ar.full, count: `${list.length} measure${list.length === 1 ? '' : 's'}`,
          benchmark: bench, buckets: bucketize(mergePeriods(list.map((a) => a.periods)), grain, year, bench, 'higher'),
          onOpen: () => setPath({ area: ar.key, measure: null, kase: null }) };
      });
    }
    if (level === 'area') {
      return inFocus.map((a) => ({ key: a.id, title: a.label, subtitle: a.entity,
        count: a.cases.size > 1 ? `${a.cases.size} case types` : undefined,
        benchmark: a.benchmark, unit: a.unit, direction: a.direction, buckets: bucketize(a.periods, grain, year, a.benchmark, a.direction),
        onOpen: () => setPath({ area: a.area, measure: a.id, kase: null }) }));
    }
    if (level === 'measure') {
      return caseSeries.map((c) => ({ key: c.label, title: c.label, subtitle: focusMeasure.label, color: c.color,
        benchmark: c.benchmark, unit: focusMeasure.unit, direction: focusMeasure.direction, buckets: c.buckets,
        onOpen: () => setPath({ ...path, kase: c.label }) }));
    }
    return [];
  }, [level, presentAreas, scoped, inFocus, caseSeries, grain, year, focusMeasure, path]);

  // ── Table rows: a tree from the current level down. ──────────────────────
  const tableRows = useMemo(() => {
    const rows = [];
    const pushMeasure = (a, depth) => {
      const hasKids = a.cases.size > 0;
      rows.push({ id: `m:${a.id}`, depth, label: a.label, sub: a.entity, benchmark: a.benchmark, unit: a.unit, direction: a.direction, hasKids,
        buckets: bucketize(a.periods, grain, year, a.benchmark, a.direction), kind: 'Measure' });
      if (hasKids && open.has(`m:${a.id}`)) {
        [...a.cases.values()].forEach((c, i) => rows.push({
          id: `c:${a.id}:${c.label}`, depth: depth + 1, label: c.label, sub: null, color: CASE_COLORS[i % CASE_COLORS.length],
          benchmark: c.target ?? a.benchmark, unit: a.unit, direction: a.direction, hasKids: false, kind: 'Case type',
          buckets: bucketize(c.periods, grain, year, c.target ?? a.benchmark, a.direction),
        }));
      }
    };
    if (level === 'program') {
      for (const ar of presentAreas) {
        const list = scoped.filter((a) => a.area === ar.key);
        const bs = [...new Set(list.map((a) => a.benchmark).filter((b) => b !== null))];
        const bench = bs.length === 1 ? bs[0] : null;
        rows.push({ id: `a:${ar.key}`, depth: 0, label: ar.label, sub: ar.full, benchmark: bench, hasKids: true, kind: 'Program area',
          buckets: bucketize(mergePeriods(list.map((a) => a.periods)), grain, year, bench, 'higher') });
        if (open.has(`a:${ar.key}`)) list.forEach((a) => pushMeasure(a, 1));
      }
    } else if (level === 'area') {
      inFocus.forEach((a) => pushMeasure(a, 0));
    } else if (focusMeasure) {
      const a = focusMeasure;
      rows.push({ id: `m:${a.id}`, depth: 0, label: a.label, sub: a.entity, benchmark: a.benchmark, unit: a.unit, direction: a.direction, hasKids: false, kind: 'Measure',
        buckets: bucketize(a.periods, grain, year, a.benchmark, a.direction) });
      [...a.cases.values()].filter((c) => !focusCase || c.label === focusCase.label).forEach((c, i) => rows.push({
        id: `c:${a.id}:${c.label}`, depth: 1, label: c.label, sub: null, color: CASE_COLORS[i % CASE_COLORS.length],
        benchmark: c.target ?? a.benchmark, unit: a.unit, direction: a.direction, hasKids: false, kind: 'Case type',
        buckets: bucketize(c.periods, grain, year, c.target ?? a.benchmark, a.direction),
      }));
    }
    return rows;
  }, [level, presentAreas, scoped, inFocus, focusMeasure, focusCase, grain, year, open]);

  const tableColumns = useMemo(() => {
    const keys = new Map();
    for (const r of tableRows) for (const b of r.buckets) keys.set(b.key, b.label);
    return [...keys.entries()].sort(([x], [y]) => x.localeCompare(y)).map(([key, label]) => ({ key, label }));
  }, [tableRows]);

  const toggleRow = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => {
    const ids = new Set();
    for (const ar of presentAreas) ids.add(`a:${ar.key}`);
    for (const a of scoped) if (a.cases.size) ids.add(`m:${a.id}`);
    setOpen(ids);
  };

  // Everything under the current level, every row open, one line per thing —
  // what both downloads print. Depth carries the tree: area 0, measure 1,
  // case type 2 (or measure 0, case type 1 inside an area or a measure).
  const collectAllRows = () => {
    const all = [];
    const addMeasure = (a, depth) => {
      all.push({ kind: 'Measure', label: a.label, sub: a.entity, depth, benchmark: a.benchmark, unit: a.unit, direction: a.direction, buckets: bucketize(a.periods, grain, year, a.benchmark, a.direction) });
      [...a.cases.values()].filter((c) => !focusCase || c.label === focusCase.label).forEach((c) => all.push({
        kind: 'Case type', label: c.label, sub: null, depth: depth + 1, benchmark: c.target ?? a.benchmark, unit: a.unit, direction: a.direction,
        buckets: bucketize(c.periods, grain, year, c.target ?? a.benchmark, a.direction),
      }));
    };
    if (level === 'program') {
      for (const ar of presentAreas) {
        const sub = scoped.filter((a) => a.area === ar.key);
        const bs = [...new Set(sub.map((a) => a.benchmark).filter((b) => b !== null))];
        all.push({ kind: 'Program area', label: ar.label, sub: ar.full, depth: 0, benchmark: bs.length === 1 ? bs[0] : null, unit: 'percent', buckets: bucketize(mergePeriods(sub.map((a) => a.periods)), grain, year, bs.length === 1 ? bs[0] : null, 'higher') });
        sub.forEach((a) => addMeasure(a, 1));
      }
    } else inFocus.forEach((a) => addMeasure(a, 0));
    const cols = new Map(); for (const r of all) for (const b of r.buckets) cols.set(b.key, b.label);
    const columns = [...cols.entries()].sort(([x], [y]) => x.localeCompare(y)).map(([key, label]) => ({ key, label }));
    return { rows: all, columns };
  };
  const scopeName = level === 'program' ? 'All areas' : level === 'area' ? areaLabelOf(path.area) : focusCase ? `${focusMeasure.label} - ${focusCase.label}` : focusMeasure.label;
  const yearLabel = year === 'all' ? 'All years' : year;

  const exportTable = () => {
    const { rows, columns } = collectAllRows();
    const header = ['Level', 'Name', 'Entity', 'Benchmark', ...columns.map((c) => c.label), 'Latest', 'Change vs prior'];
    const body = rows.map((r) => {
      const byKey = new Map(r.buckets.map((b) => [b.key, b]));
      const cur = r.buckets[r.buckets.length - 1]; const prev = r.buckets[r.buckets.length - 2];
      return [r.kind, `${'   '.repeat(r.depth)}${r.label}`, r.kind === 'Measure' ? r.sub || '' : '', r.benchmark === null ? '' : fmtMeasure(r.benchmark, r.unit),
        ...columns.map((c) => { const b = byKey.get(c.key); return b && b.rate !== null ? `${fmtMeasure(b.rate, r.unit)}${b.ok === false ? ' (missed)' : b.ok ? ' (met)' : ''}` : ''; }),
        cur && cur.rate !== null ? fmtMeasure(cur.rate, r.unit) : '', cur && prev && cur.rate !== null && prev.rate !== null ? `${Math.round((cur.rate - prev.rate) * 10) / 10}${deltaSuffix(r.unit)}` : ''];
    });
    downloadCsv(`${docTitle} - ${scopeName} - by ${grainNoun}${year !== 'all' ? ` ${year}` : ''}.csv`, [header, ...body]);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const { rows, columns } = collectAllRows();
      // The document is the table: every level under what is on screen.
      await exportChartingPdf({
        orgName, docTitle, focusTitle: scopeName, path: crumbLabels(), grainNoun, yearLabel,
        tableRows: rows, tableColumns: columns,
      });
    } finally { setPdfBusy(false); }
  };
  const crumbLabels = () => [
    'All areas',
    ...(path.area ? [areaLabelOf(path.area)] : []),
    ...(focusMeasure ? [focusMeasure.label] : []),
    ...(focusCase ? [focusCase.label] : []),
  ];

  if (loading) return <div className="py-24 text-center text-slate-400">Loading…</div>;
  if (!measures.length) {
    return (
      <div className="py-24 text-center">
        <p className="text-slate-600 font-medium">Nothing to chart yet.</p>
        <p className="text-sm text-slate-400 mt-1">{emptyHint}</p>
      </div>
    );
  }

  const baseOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { padding: 10, cornerRadius: 8 } },
    scales: {
      x: { grid: { display: false }, ticks: { color: SLATE, font: { size: 12 } } },
      y: { grid: { color: '#f1f5f9' }, border: { display: false }, ticks: { color: SLATE, font: { size: 12 } } },
    },
  };
  // Rates live on a 0-100 axis; a hold time or a count starts at zero and grows.
  const yScale = focusUnit === 'percent'
    ? { ...baseOpts.scales.y, suggestedMin: Math.max(0, (focusBenchmark ?? 80) - 20), max: 100 }
    : { ...baseOpts.scales.y, beginAtZero: true, ticks: { ...baseOpts.scales.y.ticks, callback: (v) => fmtMeasure(v, focusUnit) } };
  const benchLine = (labels, bench) => (bench === null ? [] : [{
    label: `Benchmark ${fmtMeasure(bench, focusUnit)}`, data: labels.map(() => bench), borderColor: '#f59e0b', borderWidth: 1.5, borderDash: [6, 4], pointRadius: 0, fill: false, tension: 0,
  }]);
  const visibleCaseSeries = caseSeries.filter((c) => !hiddenCases.has(c.label));
  const focusTitle = level === 'program' ? 'All areas' : level === 'area' ? areaLabelOf(path.area) : focusCase ? focusCase.label : focusMeasure.label;
  const deltaRate = current && prior && current.rate !== null && prior.rate !== null ? current.rate - prior.rate : null;
  const deltaDefs = current && prior ? current.defs - prior.defs : null;

  const crumbs = [
    { label: 'All areas', onClick: () => setPath({ area: null, measure: null, kase: null }), active: level === 'program' },
    ...(path.area ? [{ label: areaLabelOf(path.area), onClick: () => setPath({ area: path.area, measure: null, kase: null }), active: level === 'area' }] : []),
    ...(focusMeasure ? [{ label: focusMeasure.label, onClick: () => setPath({ ...path, kase: null }), active: level === 'measure' }] : []),
    ...(focusCase ? [{ label: focusCase.label, onClick: () => {}, active: true }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header + controls */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-800 tracking-tight">{title}</h2>
          <p className="text-sm text-slate-500">How each oversight measure is performing, and what sits underneath it. Click any tile to look closer.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Segmented ariaLabel="View" value={view} onChange={setView}
            options={[{ value: 'charts', label: 'Charts' }, { value: 'table', label: 'Table' }]} />
          <Segmented ariaLabel="Period" value={grain} onChange={setGrain}
            options={[{ value: 'month', label: 'Months' }, { value: 'quarter', label: 'Quarters' }, { value: 'year', label: 'Years' }]} />
          <select value={year} onChange={(e) => setYear(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
            <option value="all">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {entities.length > 1 && (
            <select value={entity} onChange={(e) => { setEntity(e.target.value); setPath({ area: null, measure: null, kase: null }); }} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
              <option value="all">Everyone</option>
              {entities.map((en) => <option key={en} value={en}>{en}</option>)}
            </select>
          )}
          {view === 'charts' && (
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer select-none" title="Lay each year over the same January to December axis">
              <input type="checkbox" checked={compareYears} onChange={(e) => setCompareYears(e.target.checked)} className="accent-orange-500" />
              Compare years
            </label>
          )}
          <button type="button" onClick={exportPdf} disabled={pdfBusy}
            title="A printed document with your logo: the headline, every chart, and the full table for what is on screen"
            className="rounded-xl bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-700 disabled:opacity-60">
            {pdfBusy ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Where you are */}
      <nav aria-label="Where you are" className="flex items-center gap-1.5 text-sm flex-wrap">
        {crumbs.map((c, i) => (
          <React.Fragment key={c.label + i}>
            {i > 0 && <span className="text-slate-300">›</span>}
            <button type="button" onClick={c.onClick} disabled={c.active}
              className={`rounded-lg px-2 py-1 ${c.active ? 'font-bold text-slate-800 bg-slate-100 cursor-default' : 'text-orange-600 hover:bg-orange-50 font-semibold'}`}>
              {c.label}
            </button>
          </React.Fragment>
        ))}
        {level !== 'program' && (
          <button type="button" onClick={() => setPath(level === 'case' ? { ...path, kase: null } : level === 'measure' ? { area: path.area, measure: null, kase: null } : { area: null, measure: null, kase: null })}
            className="ml-auto text-xs font-semibold text-slate-500 hover:text-slate-800">← Back</button>
        )}
      </nav>

      {/* Headline for the current focus */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={`${focusTitle} · this ${grainNoun}`} value={fmtMeasure(current?.rate, focusUnit)}
          tone={current?.ok === false ? 'bad' : current?.ok ? 'good' : 'slate'}
          sub={<div className="flex items-center gap-2 flex-wrap"><Pill ok={current ? current.ok : null} /><Delta value={deltaRate} suffix={deltaSuffix(focusUnit)} invert={focusDirection === 'lower'} />{current?.share && <span className="text-[11px] text-slate-500">share of measures met</span>}</div>} />
        <StatCard label="Benchmark" value={focusBenchmark === null ? 'varies' : fmtMeasure(focusBenchmark, focusUnit)} tone="brand"
          sub={<span className="text-xs text-slate-500">{focusBenchmark === null ? 'each measure keeps its own' : focusDirection === 'lower' ? 'at or below passes' : 'at or above passes'}</span>} />
        <StatCard label={`Benchmarks met · this ${grainNoun}`} value={current ? (current.share ? `${current.metVotes} of ${current.votes}` : `${current.met} of ${current.met + current.missed}`) : '—'}
          tone={current && (current.share ? current.votes - current.metVotes > 0 : current.missed > 0) ? 'bad' : 'good'}
          sub={<span className="text-xs text-slate-500">{current && (current.share ? current.votes - current.metVotes > 0 : current.missed > 0) ? `${current.share ? current.votes - current.metVotes : current.missed} missed` : 'nothing missed'}</span>} />
        <StatCard label={`Deficiencies · this ${grainNoun}`} value={current ? current.defs : '—'} tone={current && current.defs > 0 ? 'bad' : 'good'}
          sub={<Delta value={deltaDefs} suffix="" invert />} />
      </div>

      {view === 'table' ? (
        /* ── Table: a calendar grid, rows open into what is underneath ── */
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 flex-wrap">
            <p className="text-sm font-semibold text-slate-700">
              {focusTitle} by {grainNoun} <span className="text-xs font-normal text-slate-400">· {focusUnit === 'percent' ? 'rate of cases within the standard' : 'the measured result each period'}; green met the benchmark, red missed it</span>
            </p>
            <div className="flex items-center gap-2">
              {level !== 'case' && <button type="button" onClick={expandAll} className="text-xs font-semibold text-slate-600 hover:text-slate-800">Open all</button>}
              {level !== 'case' && <button type="button" onClick={() => setOpen(new Set())} className="text-xs font-semibold text-slate-600 hover:text-slate-800">Close all</button>}
              <button type="button" onClick={exportTable} className="rounded-xl bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-700">Download for Excel</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-2 font-semibold sticky left-0 bg-white min-w-[16rem]">Name</th>
                  <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Benchmark</th>
                  {tableColumns.map((c) => <th key={c.key} className="text-right px-3 py-2 font-semibold whitespace-nowrap">{c.label}</th>)}
                  <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Change</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => {
                  const byKey = new Map(r.buckets.map((b) => [b.key, b]));
                  const cur = r.buckets[r.buckets.length - 1]; const prev = r.buckets[r.buckets.length - 2];
                  const delta = cur && prev && cur.rate !== null && prev.rate !== null ? cur.rate - prev.rate : null;
                  return (
                    <tr key={r.id} className={`border-t border-slate-100 ${r.depth === 0 ? 'bg-slate-50/60' : ''}`}>
                      <td className="px-4 py-2 sticky left-0 bg-inherit">
                        <div className="flex items-center gap-2" style={{ paddingLeft: r.depth * 18 }}>
                          {r.hasKids ? (
                            <button type="button" onClick={() => toggleRow(r.id)} className="w-5 h-5 rounded-md text-slate-500 hover:bg-slate-200 text-xs font-bold" aria-label={open.has(r.id) ? 'Close' : 'Open'}>
                              {open.has(r.id) ? '−' : '+'}
                            </button>
                          ) : <span className="w-5 inline-block" />}
                          {r.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.color }} />}
                          <div className="min-w-0">
                            <p className={r.depth === 0 ? 'font-semibold text-slate-800' : 'text-slate-700'}
                              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              title={r.label}>{r.label}</p>
                            {r.sub && <p className="text-[11px] text-slate-400 truncate" title={r.sub}>{r.sub}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500 text-xs">{r.benchmark === null ? '—' : fmtMeasure(r.benchmark, r.unit)}</td>
                      {tableColumns.map((c) => {
                        const b = byKey.get(c.key);
                        if (!b || b.rate === null) return <td key={c.key} className="px-3 py-2 text-right text-slate-300">·</td>;
                        const tone = b.ok === false ? 'bg-rose-50 text-rose-700' : b.ok ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700';
                        return (
                          <td key={c.key} className="px-1.5 py-1 text-right">
                            <span className={`inline-block rounded-lg px-1.5 py-0.5 tabular-nums font-semibold ${tone}`}
                              title={b.den > 0 && r.unit === 'percent' ? `${b.num} of ${b.den} within the standard${b.defs ? ` · ${b.defs} deficienc${b.defs === 1 ? 'y' : 'ies'}` : ''}` : `Result ${fmtMeasure(b.rate, r.unit)}`}>
                              {fmtMeasure(b.rate, r.unit)}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right whitespace-nowrap"><Delta value={delta} suffix={deltaSuffix(r.unit)} invert={r.direction === 'lower'} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          {/* What sits underneath: tiles */}
          {tiles.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-3">
                {level === 'program' ? 'Program areas' : level === 'area' ? `Measures in ${areaLabelOf(path.area)}` : `Case types in ${focusMeasure.label}`}
                <span className="text-xs font-normal text-slate-400"> · latest {grainNoun}, change against the prior {grainNoun}</span>
              </p>
              <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {tiles.map((t) => <Tile key={t.key} {...t} />)}
              </div>
            </div>
          )}
          {level === 'measure' && tiles.length === 0 && (
            <p className="text-sm text-slate-500 rounded-2xl border border-dashed border-slate-200 p-4">This measure reports one number a {grainNoun}; there are no case types underneath it.</p>
          )}

          {/* Trend */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <p className="text-sm font-semibold text-slate-700">
                  {yoy ? `${focusTitle}, each year over the same months` : `${focusTitle}, ${grainNoun} over ${grainNoun}`}
                  <span className="text-xs font-normal text-slate-400"> · {focusUnit === 'percent' ? 'rate of cases within the standard' : 'the measured result each period'}</span>
                </p>
                {!yoy && caseSeries.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] text-slate-400 mr-1">Show:</span>
                    <button type="button" onClick={() => setHiddenCases(new Set())}
                      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${hiddenCases.size === 0 ? 'bg-slate-800 text-white ring-slate-800' : 'text-slate-600 ring-slate-200 hover:bg-slate-50'}`}>All</button>
                    {caseSeries.map((c) => {
                      const on = !hiddenCases.has(c.label);
                      return (
                        <button key={c.label} type="button"
                          onClick={() => setHiddenCases((s) => { const n = new Set(s); n.has(c.label) ? n.delete(c.label) : n.add(c.label); return n; })}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 flex items-center gap-1 ${on ? 'bg-white ring-slate-200 text-slate-700' : 'bg-slate-50 ring-slate-100 text-slate-400 line-through'}`}>
                          <span className="w-2 h-2 rounded-full" style={{ background: c.color, opacity: on ? 1 : 0.4 }} />{c.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="h-72">
                {yoy ? (
                  <Line
                    data={{
                      labels: MONTHS_SHORT,
                      datasets: [
                        ...yoy.map((y, i) => ({
                          label: y.year, data: y.rates,
                          borderColor: i === yoy.length - 1 ? ORANGE : YEAR_COLORS[i % YEAR_COLORS.length],
                          borderWidth: i === yoy.length - 1 ? 2.5 : 1.5, borderDash: i === yoy.length - 1 ? [] : [6, 4],
                          pointRadius: i === yoy.length - 1 ? 4 : 2,
                          pointBackgroundColor: i === yoy.length - 1 ? ORANGE : YEAR_COLORS[i % YEAR_COLORS.length],
                          tension: 0.3, spanGaps: true,
                        })),
                        ...benchLine(MONTHS_SHORT, focusBenchmark),
                      ],
                    }}
                    options={{ ...baseOpts, plugins: { ...baseOpts.plugins, legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } }, scales: { ...baseOpts.scales, y: yScale } }}
                  />
                ) : (
                  <Line
                    data={{
                      labels: buckets.map((b) => b.label),
                      datasets: [
                        {
                          label: focusTitle, data: buckets.map((b) => b.rate),
                          borderColor: ORANGE, backgroundColor: 'rgba(245,130,32,0.08)', borderWidth: visibleCaseSeries.length ? 3 : 2.5,
                          pointRadius: 4, pointBackgroundColor: buckets.map((b) => (b.ok === false ? ROSE : b.ok ? EMERALD : ORANGE)),
                          fill: !visibleCaseSeries.length, tension: 0.3, spanGaps: true,
                        },
                        ...visibleCaseSeries.map((c) => {
                          const byKey = new Map(c.buckets.map((b) => [b.key, b]));
                          return {
                            label: c.label, data: buckets.map((b) => byKey.get(b.key)?.rate ?? null),
                            borderColor: c.color, borderWidth: 1.75, pointRadius: 3, pointBackgroundColor: c.color, fill: false, tension: 0.3, spanGaps: true,
                          };
                        }),
                        ...benchLine(buckets.map((b) => b.label), focusBenchmark),
                      ],
                    }}
                    options={{
                      ...baseOpts,
                      plugins: { ...baseOpts.plugins, legend: { display: visibleCaseSeries.length > 0, position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
                        tooltip: { ...baseOpts.plugins.tooltip, callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y === null ? '—' : fmtMeasure(ctx.parsed.y, focusUnit)}` } } },
                      scales: { ...baseOpts.scales, y: yScale },
                    }}
                  />
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold text-slate-700 mb-4">Benchmarks met and missed, by {grainNoun}</p>
              <div className="h-56">
                <Bar
                  data={{
                    labels: buckets.map((b) => b.label),
                    datasets: [
                      { label: 'Met', data: buckets.map((b) => b.met), backgroundColor: EMERALD, borderRadius: 6, stack: 's' },
                      { label: 'Missed', data: buckets.map((b) => b.missed), backgroundColor: ROSE, borderRadius: 6, stack: 's' },
                    ],
                  }}
                  options={{ ...baseOpts, plugins: { ...baseOpts.plugins, legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } }, scales: { ...baseOpts.scales, x: { ...baseOpts.scales.x, stacked: true }, y: { ...baseOpts.scales.y, stacked: true, ticks: { ...baseOpts.scales.y.ticks, precision: 0 } } } }}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold text-slate-700 mb-4">Deficiencies, {grainNoun} over {grainNoun} <span className="text-xs font-normal text-slate-400">· lower is better</span></p>
              <div className="h-56">
                <Bar
                  data={{
                    labels: buckets.map((b) => b.label),
                    datasets: [{ label: 'Deficiencies', data: buckets.map((b) => b.defs), backgroundColor: buckets.map((b) => (b.defs > 0 ? ROSE : EMERALD)), borderRadius: 6 }],
                  }}
                  options={{ ...baseOpts, scales: { ...baseOpts.scales, y: { ...baseOpts.scales.y, ticks: { ...baseOpts.scales.y.ticks, precision: 0 } } } }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
